import { dispatch } from "../../state/store";
import { pushApps, setAppsError, setAppsMarkets } from "../../state/ducks";

import { responseUtil } from "../../utils";

import httpClient from "../httpClient";
import { log, notificationService, t } from "../index";
import { strCode } from "../../consts";

const apiUrl = process.env.REACT_APP_API_URL;
const cloudfrontLocalizationUrl = process.env.REACT_APP_CLOUDFRONT_LOCALIZATION_API;

const getRequestData = async (endpoint) => {
  return `${apiUrl}/app-type-management/${endpoint}`;
};

const getLocalizationRequestData = async (endpoint) => {
  return `${apiUrl}/${endpoint}`;
};

const fetchApps = async () => {
  const url = await getRequestData("get-app-types");

  const data = await httpClient.get(url);

  dispatch(pushApps(data.data ?? []));

  return data?.data;
};

const fetchApp = async (appType) => {
  const baseUrl = await getRequestData("get-app-type");
  const url = `${baseUrl}?appType=${appType}`;

  const data = await httpClient.get(url);

  return data?.data;
};

const fetchMarketsForAppType = async (url, appType) => {
  try {
    return await httpClient.get(url + `?appType=${appType}`);
  } catch (err) {
    log.error("appsService: Failed to fetch markets for " + appType, err);
    notificationService.showError({
      title: t(strCode.FETCH_APPS_MARKETS_ERROR),
      message: err?.response?.data?.message ? err.response.data.message : err?.toString()
    });
  }
};

const fetchAppsMarkets = async (apps) => {
  const url = await getRequestData("get-app-type-market-configs");

  const appsMarkets = {};

  const requests = await Promise.all(
    apps?.map((app) => {
      return fetchMarketsForAppType(url, app.appType);
    })
  );

  apps?.forEach((appType) => {
    appsMarkets[appType.appType] = null;
  });

  requests.forEach((request) => {
    if (request?.data && request.data.length) {
      const appType = request.data[0].appType;

      appsMarkets[appType] = request.data;
    }
  });

  dispatch(setAppsMarkets(appsMarkets));
};

const createApp = async (data) => {
  try {
    dispatch(setAppsError(null));

    const url = await getRequestData("create-app-type");

    const res = await httpClient.post(url, { data });

    if (responseUtil.isResponseSuccessful(res)) {
      notificationService.showSuccess({
        title: t(strCode.APP_CREATED_SHORT_TITLTE),
        message: t(strCode.APP_CREATED_LONG_TITLTE, { name: data.name })
      });
    }
  } catch (e) {
    dispatch(setAppsError(e));
    log.error("appsService: Failed to create app", e);
    notificationService.showError({
      title: t(strCode.APP_CREATE_ERROR_TITLE),
      message: e?.response?.data?.message ? e.response.data.message : e?.toString()
    });

    throw e;
  }
};

const updateApp = async (data, errNotificationTitle) => {
  try {
    dispatch(setAppsError(null));
    const url = await getRequestData("update-app-type");

    return await httpClient.put(url, { data });
  } catch (e) {
    dispatch(setAppsError(e));
    log.error("appsService: Failed to update app", e);
    notificationService.showError({
      title: t(errNotificationTitle || strCode.APP_UPDATE_ERROR_TITLE),
      message: e?.response?.data?.message ? e.response.data.message : e?.toString()
    });
  }
};

const deleteApp = async (appType) => {
  try {
    dispatch(setAppsError(null));
    const url = await getRequestData("delete-app-type");

    return await httpClient.remove(url, { data: { appType: appType } });
  } catch (e) {
    log.error("appsService: Failed to delete app", e);
    notificationService.showError({
      title: t(strCode.DELETE_APP_ERROR_TITLE),
      message: e?.response?.data?.message ? e.response.data.message : e?.toString()
    });
  }
};

const createAppRoles = async (data) => {
  try {
    const url = await getRequestData("create-app-type-group-config");

    const res = await httpClient.post(url, { data });

    if (responseUtil.isResponseSuccessful(res)) {
      notificationService.showSuccess({
        title: t(strCode.APP_ROLE_CREATE_TITLE_SHORT),
        message: t(strCode.APP_ROLE_CREATE_TITLE_LONG)
      });
    }
  } catch (e) {
    notificationService.showError({
      title: t(strCode.APP_ROLE_CREATE_ERROR_TITLE),
      message: e?.response?.data?.message ? e.response.data.message : e?.toString()
    });

    throw e;
  }
};

const updateRoles = async (data) => {
  const url = await getRequestData("update-app-type-group-config");

  return await httpClient.put(url, { data });
};

const updateRoleName = async (data) => {
  const url = await getRequestData("update-group-name");

  return await httpClient.put(url, { data });
};

const deleteRole = async (data) => {
  try {
    const url = await getRequestData("delete-app-type-group-config");

    const res = await httpClient.remove(url, { data });

    if (responseUtil.isResponseSuccessful(res)) {
      notificationService.showSuccess({
        title: t(strCode.APP_ROLE_DELETE_TITLE_SHORT),
        message: t(strCode.APP_ROLE_DELETE_TITLE_LONG)
      });
    }
  } catch (e) {
    log.error("appsService: Failed to delete app role", e);
    notificationService.showError({
      title: t(strCode.APP_ROLE_DELETE_TITLE_ERROR),
      message: e?.response?.data?.message ? e.response.data.message : e?.toString()
    });
  }
};

const createAppTypeMarketConfig = async (data) => {
  const url = await getRequestData("create-app-type-market-config");

  return await httpClient.post(url, { data });
};

const updateAppTypeMarketConfig = async (data) => {
  const url = await getRequestData("update-app-type-market-config");

  return await httpClient.put(url, { data });
};

const deleteAppTypeMarketConfig = async (data) => {
  const url = await getRequestData("delete-app-type-market-config");

  return await httpClient.remove(url, { data });
};

const getLocalization = async (localizationPath) => {
  const url = `${cloudfrontLocalizationUrl}/${localizationPath}`;

  return await httpClient.get(url);
};

const uploadLocalization = async (appType, localeName, name, isDefault, data) => {
  const url = await getLocalizationRequestData(
    `upload-localization?appType=${appType}&language=${localeName}&isDefault=${isDefault}&name=${name}`
  );

  return await httpClient.post(url, { data });
};

const deleteLocalization = async (data) => {
  const url = await getLocalizationRequestData(`delete-localization`);

  return await httpClient.remove(url, { data });
};

const appsService = {
  fetchApps,
  fetchApp,
  createApp,
  updateApp,
  fetchAppsMarkets,
  createAppTypeMarketConfig,
  updateAppTypeMarketConfig,
  deleteAppTypeMarketConfig,
  createAppRoles,
  updateRoles,
  updateRoleName,
  deleteRole,
  getLocalization,
  uploadLocalization,
  deleteLocalization,
  deleteApp
};

export default appsService;
